<template>
  <div class="rm">
    <span v-for="minutes in modelValue" :key="minutes" class="rm__chip">
      🔔 {{ describe(minutes) }}
      <button type="button" class="rm__x" :aria-label="t('calendar.reminders.remove')" @click="remove(minutes)">×</button>
    </span>
    <UiSelect
      v-if="modelValue.length < 5"
      class="rm__add"
      :model-value="''"
      :options="options"
      :placeholder="t('calendar.reminders.add')"
      @update:model-value="add"
    />
    <div v-if="custom" class="rm__custom">
      <UiInput v-model.number="amount" type="number" min="1" class="rm__amount" />
      <UiSelect v-model="unit" class="rm__unit" :options="unitOptions" />
      <UiButton size="sm" @click="addCustom">{{ t('calendar.reminders.addCustom') }}</UiButton>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiButton, UiInput, UiSelect } from '@general/ui-kit'

import { REMINDER_PRESETS, MAX_REMINDER_MINUTES } from '../lib/constants.mjs'

/** Up to five reminders, minutes before; the usual ones a tap, any other typed. */
const props = defineProps({ modelValue: { type: Array, default: () => [] } })
const emit = defineEmits(['update:modelValue'])
const { t } = useI18n()

const custom = ref(false)
const amount = ref(2)
const unit = ref('hours')
const UNITS = { minutes: 1, hours: 60, days: 1440 }

function describe(minutes) {
  if (minutes === 0) return t('calendar.reminders.atStart')
  if (minutes % 1440 === 0) return t('calendar.reminders.days', { count: minutes / 1440 }, minutes / 1440)
  if (minutes % 60 === 0) return t('calendar.reminders.hours', { count: minutes / 60 }, minutes / 60)
  return t('calendar.reminders.minutes', { count: minutes }, minutes)
}
const options = computed(() => [
  ...REMINDER_PRESETS.filter(minutes => !props.modelValue.includes(minutes)).map(minutes => ({ value: String(minutes), label: describe(minutes) })),
  { value: 'custom', label: t('calendar.reminders.custom') },
])
const unitOptions = computed(() => Object.keys(UNITS).map(key => ({ value: key, label: t(`calendar.reminders.units.${key}`) })))

function add(value) {
  if (value === 'custom') { custom.value = true; return }
  if (value === '' || value === null) return
  push(Number(value))
}
function addCustom() {
  const minutes = Math.round(Number(amount.value) * UNITS[unit.value])
  if (!Number.isFinite(minutes) || minutes < 0 || minutes > MAX_REMINDER_MINUTES) return
  push(minutes)
  custom.value = false
}
function push(minutes) {
  if (props.modelValue.includes(minutes)) return
  emit('update:modelValue', [...props.modelValue, minutes].sort((a, b) => b - a))
}
function remove(minutes) {
  emit('update:modelValue', props.modelValue.filter(value => value !== minutes))
}
</script>

<style scoped>
.rm { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.rm__chip { display: inline-flex; align-items: center; gap: 4px; padding: 3px 4px 3px 10px; border-radius: var(--radius-full); background: var(--color-surface-2); border: 1px solid var(--color-border); font-size: var(--text-sm); }
.rm__x { border: 0; background: none; cursor: pointer; width: 26px; height: 26px; border-radius: 50%; color: var(--color-text-soft); font-size: 15px; }
.rm__x:hover { background: var(--color-gray-100); }
.rm__add { min-width: 180px; }
.rm__custom { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; width: 100%; }
.rm__amount { width: 84px; }
.rm__unit { min-width: 120px; }
</style>
